import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { theme } from '../../../theme';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardBody } from '../../../components/ui/Card';
import {
  Complain,
  createComplain,
  fetchComplainsForBranch,
} from '../service/complainService';
import { verifyBranchPinForPath, BranchPinLookupData } from '../service/branchPinLookupService';
import { Table } from '../../../components/ui/Table';

const PageWrap = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.secondary[50]} 100%);
  padding: ${theme.spacing[8]} ${theme.spacing[4]};
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const Sub = styled.p`
  color: ${theme.colors.light.text.secondary};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const PinCard = styled(Card)`
  max-width: 420px;
  margin: 0 auto;
`;

const ErrorBox = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.accent.danger}15;
  border: 1px solid ${theme.colors.accent.danger}40;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[4]};
`;

const FormGrid = styled.div`
  display: grid;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const sessionKey = (companyId: string, bankId: string, branchId: string) =>
  `branch_complains_unlock_${companyId}_${bankId}_${branchId}`;

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const PublicBranchComplainsPage: React.FC = () => {
  const { companyId = '', bankId = '', branchId = '' } = useParams<{
    companyId: string;
    bankId: string;
    branchId: string;
  }>();

  const [pinInput, setPinInput] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [meta, setMeta] = useState<BranchPinLookupData | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [rows, setRows] = useState<Complain[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !companyId || !bankId || !branchId) return;
    const v = sessionStorage.getItem(sessionKey(companyId, bankId, branchId));
    if (v === '1') setUnlocked(true);
  }, [companyId, bankId, branchId]);

  useEffect(() => {
    if (!unlocked || !companyId || !bankId || !branchId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingList(true);
        const list = await fetchComplainsForBranch(companyId, bankId, branchId);
        if (!cancelled) setRows(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unlocked, companyId, bankId, branchId]);

  const tryPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError(null);
    try {
      const ok = await verifyBranchPinForPath(pinInput, companyId, bankId, branchId);
      if (!ok) {
        setPinError('Invalid PIN. Please check your branch PIN or contact support.');
        return;
      }
      sessionStorage.setItem(sessionKey(companyId, bankId, branchId), '1');
      setMeta(ok);
      setUnlocked(true);
      toast.success('PIN verified');
    } catch (err) {
      console.error(err);
      setPinError('Could not verify PIN. If this persists, contact support.');
    }
  };

  const submitComplain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!type.trim() || !description.trim()) {
      toast.error('Please enter type and description');
      return;
    }
    try {
      setSubmitting(true);
      await createComplain({
        companyId,
        bankId,
        branchId,
        bankName: meta?.bankName,
        address: meta?.address,
        type: type.trim(),
        description: description.trim(),
      });
      toast.success('Complain submitted');
      setType('');
      setDescription('');
      const list = await fetchComplainsForBranch(companyId, bankId, branchId);
      setRows(list);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!companyId || !bankId || !branchId) {
    return (
      <PageWrap>
        <Inner>
          <ErrorBox>Invalid link.</ErrorBox>
        </Inner>
      </PageWrap>
    );
  }

  if (!unlocked) {
    return (
      <PageWrap>
        <Inner>
          <Title style={{ textAlign: 'center' }}>Branch complains</Title>
          <Sub style={{ textAlign: 'center' }}>
            Enter your branch PIN (same as on your branch record) to view or submit a complain.
          </Sub>
          <PinCard>
            <CardBody>
              <form onSubmit={tryPin}>
                {pinError && <ErrorBox>{pinError}</ErrorBox>}
                <FormGrid>
                  <Input
                    label="Branch PIN"
                    type="password"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    placeholder="e.g. H58581160"
                  />
                </FormGrid>
                <Button type="submit" fullWidth>
                  Continue
                </Button>
                <p
                  style={{
                    marginTop: theme.spacing[4],
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.light.text.secondary,
                    textAlign: 'center',
                  }}
                >
                  Wrong PIN? Contact your security company for assistance.
                </p>
              </form>
            </CardBody>
          </PinCard>
        </Inner>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <Inner>
        <Title>Complains</Title>
        <Sub>
          {meta?.bankName ? `${meta.bankName} — ` : ''}
          {meta?.address || 'Branch portal'}
          {meta?.branchCode != null && meta.branchCode !== '' ? ` · Code ${meta.branchCode}` : ''}
        </Sub>

        <div style={{ marginBottom: theme.spacing[6] }}>
          <Card>
          <CardBody>
            <h2 style={{ margin: `0 0 ${theme.spacing[4]} 0`, fontSize: theme.typography.fontSize.lg }}>New complain</h2>
            <form onSubmit={submitComplain}>
              <FormGrid>
                <Input label="Type" value={type} onChange={(e) => setType(e.target.value)} placeholder="e.g. Deployment, Equipment" />
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.sm,
                      marginBottom: theme.spacing[1],
                      color: theme.colors.light.text.secondary,
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: theme.spacing[3],
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.colors.light.border.medium}`,
                      fontFamily: theme.typography.fontFamily.primary,
                    }}
                    placeholder="Describe the issue…"
                  />
                </div>
              </FormGrid>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit complain'}
              </Button>
            </form>
          </CardBody>
          </Card>
        </div>

        <Card>
          <CardBody>
            <h2 style={{ margin: `0 0 ${theme.spacing[4]} 0`, fontSize: theme.typography.fontSize.lg }}>Your complains</h2>
            {loadingList ? (
              <p style={{ color: theme.colors.light.text.secondary }}>Loading…</p>
            ) : rows.length === 0 ? (
              <p style={{ color: theme.colors.light.text.secondary }}>No complains submitted yet.</p>
            ) : (
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>ID</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Description</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.map((r) => (
                    <Table.Row key={r.id}>
                      <Table.Cell style={{ fontFamily: 'monospace', fontSize: theme.typography.fontSize.sm }}>{r.id}</Table.Cell>
                      <Table.Cell>{r.type}</Table.Cell>
                      <Table.Cell style={{ maxWidth: 280 }}>{r.description}</Table.Cell>
                      <Table.Cell>{formatStatus(r.status)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </CardBody>
        </Card>
      </Inner>
    </PageWrap>
  );
};

export default PublicBranchComplainsPage;
